import React, { useState } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import { Download, Landmark, Layers, Search as SearchIcon, ShieldCheck, Trophy, UsersRound } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Avatar, Pagination, RankChip, Select } from "../../components/ui";
import { RankingsSidebar } from "./Rankings";
import { useApi } from "../../api/client";
import { Shooter } from "../../data/types";
import { downloadCsv } from "../../utils/download";
import { formatScore, formatAverage } from "../../utils/score";

type Mode = "shooter" | "aggregate";

interface TypeConfig {
  title: string;
  desc: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  mode: Mode;
  filter?: (s: Shooter) => boolean;
  aggregateKey?: (s: Shooter) => string;
  aggregateLabel?: string;
  note: string;
}

/*
 * Boards that are not a placing in their own right, and where the reader should
 * go instead.
 *
 * A grade divides a class — an A-grade Target Rifle shooter is not competing
 * against an A-grade F-Class shooter — so ranking by grade alone puts scores
 * from different scales on one ladder. The Class Rankings page splits by grade
 * within each class, which is the placing a competitor is actually awarded.
 */
const REDIRECTS: Record<string, string> = {
  grade: "/rankings/class?split=grade",
};

const CONFIG: Record<string, TypeConfig> = {
  category: {
    title: "Category Rankings",
    desc: "Rankings across shooter categories — Rifle Shooter, Lady Shooter and Junior.",
    icon: Layers,
    mode: "shooter",
    note: "A shooter's category is set on their competitor profile and may qualify them for category prizes.",
  },
  teams: {
    title: "Teams / Clubs Rankings",
    desc: "Team standings built from club and state-team performance.",
    icon: UsersRound,
    mode: "aggregate",
    aggregateKey: (s) => s.team || s.club,
    aggregateLabel: "Team / Club",
    note: "Where a shooter has a declared team it counts here; otherwise their club represents them.",
  },
};

interface AggRow {
  name: string;
  members: number;
  states: string;
  avgScore: number;
  bestScore: number;
  competitions: number;
}

export default function RankingsByType() {
  const navigate = useNavigate();
  const { type } = useParams();
  const cfg = CONFIG[type || ""];
  const { data: shooters } = useApi<Shooter[]>("/shooters", []);
  const [search, setSearch] = useState("");

  const redirect = REDIRECTS[type || ""];
  if (redirect) return <Navigate to={redirect} replace />;
  if (!cfg) return <Navigate to="/rankings" replace />;

  const q = search.trim().toLowerCase();
  const filtered = (cfg.filter ? shooters.filter(cfg.filter) : [...shooters])
    .filter((s) => !q || s.name.toLowerCase().includes(q) || s.sid.includes(q))
    .sort((a, b) => b.avgScore - a.avgScore);

  const aggRows: AggRow[] = (() => {
    if (cfg.mode !== "aggregate") return [];
    const map = new Map<string, Shooter[]>();
    shooters.forEach((s) => {
      const key = cfg.aggregateKey!(s);
      map.set(key, [...(map.get(key) || []), s]);
    });
    return Array.from(map.entries())
      .map(([name, members]) => ({
        name,
        members: members.length,
        states: Array.from(new Set(members.map((m) => m.state))).join(", "),
        avgScore: members.reduce((a, m) => a + m.avgScore, 0) / members.length,
        bestScore: Math.max(...members.map((m) => m.bestScore)),
        competitions: members.reduce((a, m) => a + m.competitions, 0),
      }))
      .sort((a, b) => b.avgScore - a.avgScore);
  })();

  const top = cfg.mode === "shooter" ? filtered[0] : undefined;
  const topAgg = cfg.mode === "aggregate" ? aggRows[0] : undefined;

  const tiles =
    cfg.mode === "shooter"
      ? [
          { label: "Shooters Ranked", value: filtered.length },
          { label: "Top Average Score", value: formatAverage(filtered[0]?.avgScore) },
          { label: "Best Score", value: filtered.length ? formatScore(Math.max(...filtered.map((s) => s.bestScore))) : "—" },
          { label: "Date Range", value: "All Time" },
        ]
      : [
          { label: `${cfg.aggregateLabel}s Ranked`, value: aggRows.length },
          { label: "Shooters Counted", value: shooters.length },
          { label: "Top Average", value: formatAverage(aggRows[0]?.avgScore) },
          { label: "Date Range", value: "All Time" },
        ];

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-5">
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Rankings", to: "/rankings" }, { label: cfg.title }]} />

      <div className="mt-3 flex flex-col gap-4 lg:flex-row">
        <RankingsSidebar title="Rankings" subtitle="View rankings by class, grade, category and more." />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="flex items-center gap-3 text-2xl font-extrabold tracking-tight text-ink">
                <cfg.icon size={30} className="text-primary" /> {cfg.title}
              </h1>
              <p className="mt-1 text-[13px] text-muted">{cfg.desc}</p>
            </div>
            <button
              onClick={() =>
                cfg.mode === "shooter"
                  ? downloadCsv(
                      `${type}-rankings.csv`,
                      ["Rank", "Shooter", "SID", "Club", "Class", "Grade", "Category", "Events", "Average", "Best"],
                      filtered.map((s, i) => [i + 1, s.name, s.sid, s.club, s.rifleClass, s.grade, s.category, s.competitions, formatAverage(s.avgScore), formatScore(s.bestScore)])
                    )
                  : downloadCsv(
                      `${type}-rankings.csv`,
                      ["Rank", cfg.aggregateLabel || "Group", "States", "Shooters", "Events", "Average", "Best"],
                      aggRows.map((r, i) => [i + 1, r.name, r.states, r.members, r.competitions, formatAverage(r.avgScore), formatScore(r.bestScore)])
                    )
              }
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-slate-50"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>

          {/* Summary tiles */}
          <div className="card mt-4 grid grid-cols-2 divide-x divide-line lg:grid-cols-4">
            {tiles.map((t) => (
              <div key={t.label} className="px-5 py-5">
                <div className="text-[11px] font-bold uppercase tracking-wide text-muted">{t.label}</div>
                <div className="mt-1 text-2xl font-extrabold text-ink">{t.value}</div>
              </div>
            ))}
          </div>

          <div className="card mt-4 flex flex-wrap items-center gap-3 p-4">
            <div className="relative w-64 flex-1">
              <SearchIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="input pl-9" placeholder="Search by name or SID..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select options={["All Events"]} className="w-40" />
            <Select options={["All Time", "This Year"]} className="w-full sm:w-32" />
          </div>

          {/* Table */}
          <div className="card mt-4 overflow-x-auto">
            {cfg.mode === "shooter" ? (
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="border-b border-line bg-slate-50">
                    <th className="th">Rank</th>
                    <th className="th">Shooter</th>
                    <th className="th">SID</th>
                    <th className="th">Class</th>
                    <th className="th">Grade</th>
                    <th className="th">Category</th>
                    <th className="th text-right">Events</th>
                    <th className="th text-right">Average Score</th>
                    <th className="th text-right">Personal Best</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {filtered.map((s, i) => (
                    <tr key={s.sid} onClick={() => navigate(`/shooters/${s.sid}`)} className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                      <td className="td"><RankChip rank={i + 1} /></td>
                      <td className="td">
                        <div className="flex items-center gap-3">
                          <Avatar name={s.name} />
                          <div>
                            <Link to={`/shooters/${s.sid}`} className="font-bold text-ink hover:text-primary">{s.name}</Link>
                            <div className="text-xs text-muted">{s.club}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td text-muted">{s.sid}</td>
                      <td className="td">{s.rifleClass}</td>
                      <td className="td">
                        <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${
                          s.grade === "A" ? "bg-success-soft text-success" : s.grade === "B" ? "bg-primary-soft text-primary" : "bg-slate-100 text-slate-500"
                        }`}>
                          {s.grade}
                        </span>
                      </td>
                      <td className="td text-slate-600">{s.category}</td>
                      <td className="td text-right">{s.competitions}</td>
                      <td className="td text-right font-bold">{formatAverage(s.avgScore)}</td>
                      <td className="td text-right font-bold text-primary">{formatScore(s.bestScore)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="border-b border-line bg-slate-50">
                    <th className="th">Rank</th>
                    <th className="th">{cfg.aggregateLabel}</th>
                    <th className="th">State(s)</th>
                    <th className="th text-right">Active Shooters</th>
                    <th className="th text-right">Events</th>
                    <th className="th text-right">Average Score</th>
                    <th className="th text-right">Best Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {aggRows.map((r, i) => (
                    <tr key={r.name} className={`transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                      <td className="td"><RankChip rank={i + 1} /></td>
                      <td className="td">
                        <span className="flex items-center gap-3 font-bold"><Landmark size={16} className="text-primary" /> {r.name}</span>
                      </td>
                      <td className="td text-slate-600">{r.states}</td>
                      <td className="td text-right">{r.members}</td>
                      <td className="td text-right">{r.competitions}</td>
                      <td className="td text-right font-bold">{formatAverage(r.avgScore)}</td>
                      <td className="td text-right font-bold text-primary">{formatScore(r.bestScore)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="border-t border-line">
              <Pagination
                summary={`Showing 1 to ${cfg.mode === "shooter" ? filtered.length : aggRows.length} of ${cfg.mode === "shooter" ? filtered.length : aggRows.length}`}
                pages={1}
              />
            </div>
          </div>
        </div>

        {/* Right column */}
        <aside className="w-full shrink-0 space-y-6 lg:w-[268px]">
          {(top || topAgg) && (
            <div className="card border-success/30 bg-success-soft/60 p-4">
              <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink">
                <Trophy size={16} className="text-success" /> Current Leader
              </h4>
              {top ? (
                <>
                  <div className="mt-4 flex items-center gap-3.5">
                    <Avatar name={top.name} size={52} />
                    <div>
                      <div className="text-[15px] font-bold text-ink">{top.name}</div>
                      <div className="text-[13px] text-muted">{top.club}</div>
                      <span className="mt-1.5 inline-block rounded-md bg-success px-2 py-0.5 text-[10px] font-extrabold text-white">RANK #1</span>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 border-t border-success/20 pt-4 text-sm">
                    <div className="flex justify-between"><span className="text-muted">Average Score</span><span className="font-bold text-ink">{formatAverage(top.avgScore)}</span></div>
                    <div className="flex justify-between"><span className="text-muted">Personal Best</span><span className="font-bold text-ink">{formatScore(top.bestScore)}</span></div>
                    <div className="flex justify-between"><span className="text-muted">Competitions</span><span className="font-bold text-ink">{top.competitions}</span></div>
                  </div>
                  <Link to={`/shooters/${top.sid}`} className="mt-4 block text-sm font-bold text-primary hover:underline">View Shooter Profile →</Link>
                </>
              ) : (
                topAgg && (
                  <>
                    <div className="mt-4 flex items-center gap-3.5">
                      <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-success/15 text-success"><Landmark size={24} /></span>
                      <div>
                        <div className="text-[15px] font-bold text-ink">{topAgg.name}</div>
                        <div className="text-[13px] text-muted">{topAgg.states}</div>
                        <span className="mt-1.5 inline-block rounded-md bg-success px-2 py-0.5 text-[10px] font-extrabold text-white">RANK #1</span>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2 border-t border-success/20 pt-4 text-sm">
                      <div className="flex justify-between"><span className="text-muted">Average Score</span><span className="font-bold text-ink">{formatAverage(topAgg.avgScore)}</span></div>
                      <div className="flex justify-between"><span className="text-muted">Best Score</span><span className="font-bold text-ink">{formatScore(topAgg.bestScore)}</span></div>
                      <div className="flex justify-between"><span className="text-muted">Active Shooters</span><span className="font-bold text-ink">{topAgg.members}</span></div>
                    </div>
                  </>
                )
              )}
            </div>
          )}

          <div className="card p-4">
            <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink">
              <ShieldCheck size={15} className="text-primary" /> How This Ranking Works
            </h4>
            <p className="mt-2.5 text-[13px] leading-relaxed text-muted">{cfg.note}</p>
            <span className="mt-3 block cursor-pointer text-[13px] font-bold text-primary hover:underline">Ranking Rules & Calculation →</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
